import React, { useState } from "react";
import { useFormikContext } from "formik";
import { Button, IconButton, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import TrashIcon from "assets/icons/Trash.svg";
import FormField from "./FormField";
import InputMask from "react-input-mask";

const Step3 = ({
  fields,
  showAddLicense,
  setShowAddLicense,
  newLicense,
  handleLicenseInputChange,
  handleAddLicense,
  handleRemoveLicense,
  showAddInsurance,
  setShowAddInsurance,
  newInsurance,
  handleAddInsurance,
  handleRemoveInsurance,
  setNewInsurance,
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [licenseErrors, setLicenseErrors] = useState({});
  const [insuranceErrors, setInsuranceErrors] = useState({});

  const licenses = values.licenses || [];
  const insurances = values.insurances || [];

  const onAddLicense = () => {
    setLicenseErrors({});
    const errs = {};
    if (!newLicense.license_name?.trim()) errs.license_name = "License name is required";
    if (!newLicense.issuing_authority?.trim()) errs.issuing_authority = "Issuing authority is required";
    if (!newLicense.jurisdiction_coverage?.trim()) errs.jurisdiction_coverage = "Jurisdiction is required";
    if (!newLicense.license_number?.trim()) errs.license_number = "License number is required";
    if (!newLicense.expiration_date) errs.expiration_date = "Expiration date is required";

    if (Object.keys(errs).length > 0) {
      setLicenseErrors(errs);
      return;
    }
    handleAddLicense(setFieldValue, values);
  };

  const onAddInsurance = () => {
    handleAddInsurance(setFieldValue, values);
  };

  return (
    <div className="w-full space-y-8 mt-4">
      {/* Licensing Section */}
      <div className="space-y-4">
        <Typography variant="h2" sx={{ color: "#242424", fontWeight: 600 }}>Licensing</Typography>
        
        {licenses.map((license, index) => {
          const itemErrors = Array.isArray(errors.licenses) ? errors.licenses[index] : null;
          return (
            <Box key={index} sx={{ borderRadius: "16px", p: 3, border: "1px solid #ECECEC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h3" sx={{ color: "#242424", fontWeight: 500 }}>{license.license_name}</Typography>
                <Typography sx={{ color: "#595959", fontSize: "14px" }}>{license.license_number} • Exp: {license.expiration_date}</Typography>
                {itemErrors && Object.keys(itemErrors).length > 0 && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                    {Object.values(itemErrors).join(", ")}
                  </Typography>
                )}
              </Box>
              <IconButton onClick={() => handleRemoveLicense(index, setFieldValue, values)} sx={{ border: "1px solid #FFE1E1", backgroundColor: "#FEF2F2", borderRadius: "12px" }}>
                <img src={TrashIcon} alt="Delete" />
              </IconButton>
            </Box>
          );
        })}

        {showAddLicense && (
          <Box sx={{ borderRadius: "16px", p: 3, border: "1px solid #ECECEC" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h3" sx={{ color: "#242424" }}>Add new license</Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button onClick={() => setShowAddLicense(false)} sx={{ color: "#242424", bgcolor: "#F8F8F8", border: "1px solid #ECECEC", borderRadius: "12px", px: 2, height: "38px" }}>Cancel</Button>
                <Button onClick={onAddLicense} sx={{ color: "#FFF", bgcolor: "#3CAB5C", borderRadius: "12px", px: 2, height: "38px", "&:hover": { bgcolor: "#2e8446" } }}>Save</Button>
              </Box>
            </Box>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: "License Name *", name: "license_name", placeholder: "e.g. Master Plumber License" },
                { label: "Issuing Authority *", name: "issuing_authority", placeholder: "e.g. Nassau County Office" },
                { label: "Jurisdiction Coverage *", name: "jurisdiction_coverage", placeholder: "e.g. Nassau County" },
                { label: "License Number *", name: "license_number", placeholder: "e.g. MP-123456" },
              ].map(f => (
                <div key={f.name}>
                  <label className="block text-[12px] font-semibold text-[#333333] mb-1">{f.label}</label>
                  <input
                    type="text"
                    placeholder={f.placeholder}
                    value={newLicense[f.name]}
                    onChange={(e) => {
                      handleLicenseInputChange(f.name, e.target.value);
                      if (licenseErrors[f.name]) setLicenseErrors(prev => ({ ...prev, [f.name]: undefined }));
                    }}
                    className={`w-full bg-[#F8F8F8] rounded-[8px] px-3 py-2 text-sm focus:outline-none ring-1 ${licenseErrors[f.name] ? "ring-red-500" : "ring-[#ECECEC]"}`}
                  />
                  {licenseErrors[f.name] && <p className="text-red-500 text-[10px] mt-1">{licenseErrors[f.name]}</p>}
                </div>
              ))}
              <div>
                <label className="block text-[12px] font-semibold text-[#333333] mb-1">Expiration Date *</label>
                <input
                  type="date"
                  value={newLicense.expiration_date}
                  onChange={(e) => handleLicenseInputChange("expiration_date", e.target.value)}
                  className={`w-full bg-[#F8F8F8] rounded-[8px] px-3 py-2 text-sm focus:outline-none ring-1 ${licenseErrors.expiration_date ? "ring-red-500" : "ring-[#ECECEC]"}`}
                />
                {licenseErrors.expiration_date && <p className="text-red-500 text-[10px] mt-1">{licenseErrors.expiration_date}</p>}
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#333333] mb-1">License Document *</label>
                <input
                  type="file"
                  onChange={(e) => handleLicenseInputChange("license_document", e.target.files[0])}
                  className={`w-full bg-[#F8F8F8] rounded-[8px] px-3 py-2 text-sm focus:outline-none ring-1 ${licenseErrors.license_document ? "ring-red-500" : "ring-[#ECECEC]"}`}
                  accept="image/*,.pdf,.doc,.docx"
                />
                {licenseErrors.license_document && <p className="text-red-500 text-[10px] mt-1">{licenseErrors.license_document}</p>}
              </div>
            </div>
          </Box>
        )}

        {!showAddLicense && (
          <Button
            sx={{ color: "#2C8D48", backgroundColor: "#F2FBF4", p: "12px 16px", width: "100%", justifyContent: "start" }}
            onClick={() => setShowAddLicense(true)}
            startIcon={<AddIcon />}
          >
            Add License
          </Button>
        )}
      </div>

      {/* <div className="space-y-4">
        <FormField
          {...fields.emergency_24_7}
          error={errors.emergency_24_7}
          touched={touched.emergency_24_7}
        />
      </div> */}

      {/* Insurance Section */}
      <div className="space-y-4">
        <Typography variant="h2" sx={{ color: "#242424", fontWeight: 600 }}>Insurance</Typography>

        {insurances.map((insurance, index) => {
          const itemErrors = Array.isArray(errors.insurances) ? errors.insurances[index] : null;
          return (
            <Box key={index} sx={{ borderRadius: "16px", p: 3, border: "1px solid #ECECEC", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ color: "#242424" }}>
                  {insurance.insurance_document?.name || (insurance.insurance_document_url ? "View Current Insurance" : `Insurance Document ${index + 1}`)}
                </Typography>
                {itemErrors && Object.keys(itemErrors).length > 0 && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                    {Object.values(itemErrors).join(", ")}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {insurance.insurance_document_url && (
                  <a href={insurance.insurance_document_url} target="_blank" rel="noreferrer" className="text-[12px] text-[#3CAB5C] underline">View</a>
                )}
                <IconButton onClick={() => handleRemoveInsurance(index, setFieldValue, values)} sx={{ border: "1px solid #FFE1E1", backgroundColor: "#FEF2F2", borderRadius: "12px" }}>
                  <img src={TrashIcon} alt="Delete" />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        {showAddInsurance && (
          <Box sx={{ borderRadius: "16px", p: 3, border: "1px solid #ECECEC" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h3" sx={{ color: "#242424" }}>Add new insurance</Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button onClick={() => setShowAddInsurance(false)} sx={{ color: "#242424", bgcolor: "#F8F8F8", border: "1px solid #ECECEC", borderRadius: "12px", px: 2, height: "38px" }}>Cancel</Button>
                <Button onClick={onAddInsurance} sx={{ color: "#FFF", bgcolor: "#3CAB5C", borderRadius: "12px", px: 2, height: "38px", "&:hover": { bgcolor: "#2e8446" } }}>Save</Button>
              </Box>
            </Box>
            <div>
              <label className="block text-[12px] font-semibold text-[#333333] mb-1">Insurance Document *</label>
              <input
                type="file"
                onChange={(e) => setNewInsurance({ insurance_document: e.target.files[0] })}
                className={`w-full bg-[#F8F8F8] rounded-[8px] px-3 py-2 text-sm focus:outline-none ring-1 ${insuranceErrors.insurance_document ? "ring-red-500" : "ring-[#ECECEC]"}`}
                accept="image/*,.pdf,.doc,.docx"
              />
              {insuranceErrors.insurance_document && <p className="text-red-500 text-[10px] mt-1">{insuranceErrors.insurance_document}</p>}
            </div>
          </Box>
        )}

        {!showAddInsurance && (
          <Button
            sx={{ color: "#2C8D48", backgroundColor: "#F2FBF4", p: "12px 16px", width: "100%", justifyContent: "start" }}
            onClick={() => setShowAddInsurance(true)}
            startIcon={<AddIcon />}
          >
            Add Insurance Document
          </Button>
        )}
      </div>
    </div>
  );
};

export default Step3;
